import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Heart, PersonStanding, Share2 } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const helpOptions = [
  {
    icon: Heart,
    title: "Spenden",
    text: "Unterstütze die Forschung und bring uns der Heilung näher.",
    cta: "Spenden",
    link: "#spenden",
  },
  {
    icon: PersonStanding,
    title: "Mitlaufen",
    text: "Lauf mit uns beim Run for Rett am 13.06.2026.",
    cta: "Zum Lauf anmelden",
    link: "#anmeldung",
  },
  {
    icon: Share2,
    title: "Teilen",
    text: "Erzähle Freunden, Familie und Kollegen vom Run for Rett.",
    cta: "Kampagne teilen",
    link: null,
  },
];

export default function HowToHelp() {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [cardsRef, cardsVisible] = useScrollAnimation({ threshold: 0.15 });

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Run for Rett – Run for Ella",
          text: "Gemeinsam laufen für eine Zukunft ohne Rett-Syndrom!",
          url: window.location.href,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <section id="helfen" data-testid="how-to-help-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          ref={headerRef}
          className={`text-center mb-12 md:mb-16 animate-fade-up ${headerVisible ? 'visible' : ''}`}
        >
          <p className="font-accent text-xl md:text-2xl mb-2" style={{ color: "#7DD4B5" }}>
            Mach den Unterschied
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight" style={{ color: "#1F2937" }}>
            So kannst du helfen
          </h2>
          <div className="section-divider mx-auto mt-4" />
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {helpOptions.map((option, idx) => {
            const Icon = option.icon;
            return (
              <Card
                key={idx}
                data-testid={`help-card-${idx}`}
                className={`border-0 shadow-md hover:shadow-xl transition-all hover:-translate-y-1 bg-white group card-animate ${cardsVisible ? 'visible' : ''}`}
                style={{ transitionDelay: `${idx * 150}ms` }}
              >
                <CardContent className="p-8 text-center h-full flex flex-col">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform mb-5"
                    style={{ background: "linear-gradient(135deg, rgba(180,110,182,0.1), rgba(149,225,187,0.1))" }}
                  >
                    <Icon className="w-8 h-8" style={{ color: "#b46eb6" }} />
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-3" style={{ color: "#1F2937" }}>
                    {option.title}
                  </h3>
                  <p className="text-sm leading-relaxed flex-grow mb-5" style={{ color: "#4B5563" }}>
                    {option.text}
                  </p>
                  <Button
                    data-testid={`help-btn-${idx}`}
                    onClick={() => option.link ? scrollTo(option.link) : handleShare()}
                    className="rounded-full px-6 font-semibold bg-[#b46eb6] hover:bg-[#8f5691] text-white shadow-md hover:shadow-lg transition-all mt-auto"
                  >
                    {option.cta}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
